/**
 * CanonicalRouteProvider - Centralized Page Registry Enforcement
 * 
 * Phase 3-C Update: Clean route classification system.
 * 
 * ALL routes fall into exactly one of 3 categories:
 * 
 * 1. CANONICAL PAGES: Governed by Page Registry + Page Builder
 *    - Homepage, series detail, novel detail, reader, creator profile, etc.
 *    - Must be registered → has definition → renders via Page Builder
 *    - Unregistered → 404. No definition → "Coming Soon" or admin CTA.
 * 
 * 2. SYSTEM_ACTION_ROUTES: Auth + Creator workflows + Admin tools
 *    - Require auth, render their own UI, never go through Page Builder
 *    - Examples: /create-series, /publish, /dashboard, /admin/*, /auth
 *    - Bypass registry enforcement permanently by design
 * 
 * 3. STATIC_ROUTES: Legal pages, health checks, utility pages
 *    - Always accessible, no auth required, no registry
 *    - Examples: /terms, /privacy, /dmca
 */

import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { useLocation, matchPath } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2,
  FileX,
  AlertTriangle,
  Construction,
  ArrowLeft,
  Home,
  Plus,
} from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Configure axios base URL
if (API_URL) {
  axios.defaults.baseURL = API_URL;
}

// Route params context for canonical pages
export const RouteParamsContext = createContext({});
export const useRouteParams = () => useContext(RouteParamsContext);

// =============================================================================
// ROUTE CLASSIFICATION
// =============================================================================

/**
 * SYSTEM ACTION ROUTES — Auth, creator workflows, admin tools.
 * These render their own UI components, require auth (except /auth itself),
 * and must NEVER be governed by Page Registry.
 * 
 * Rationale for each entry documented inline.
 */
const SYSTEM_ACTION_ROUTES = [
  // --- Authentication ---
  '/auth',                // Login/signup page
  '/auth/callback',       // OAuth callback handler
  '/reset-password',      // Password reset flow

  // --- Admin tools (all handled by admin auth guard) ---
  '/admin',               // Admin dashboard
  '/admin/*',             // All admin sub-routes (users, series, settings, page-builder, etc.)

  // --- Creator workflows (require creator auth) ---
  '/dashboard',           // Creator dashboard overview
  '/create',              // Create content hub
  '/create/*',            // Create sub-flows
  '/create-series',       // Create new series form
  '/create-novel',        // Create new novel form
  '/publish',             // Publish content hub
  '/publish/*',           // Publish sub-flows
  '/publish-hub',         // Publishing dashboard
  '/upload-artwork',      // Upload single artwork
  '/bulk-upload',         // Bulk content upload

  // --- Creator content management (require auth + ownership) ---
  '/series/:seriesId/read/:chapterId',     // Legacy episode reader (redirect target)
  '/series/:seriesId/read/:chapterId/*',   // Legacy episode reader sub-routes
  '/series/:seriesId/episode/:chapterId',  // Episode detail/edit
  '/series/:seriesId/episode/:chapterId/*',// Episode sub-routes (pages manager)
  '/series/:seriesId/upload',              // Upload episode to series
  '/series/:seriesId/upload/*',            // Upload sub-flows
  '/novel/:novelId/read/:chapterId',       // Legacy novel chapter reader
  '/novel/:novelId/read/:chapterId/*',     // Legacy novel chapter sub-routes
  '/novel/:novelId/chapter/:chapterId',    // Chapter detail/edit
  '/novel/:novelId/chapter/:chapterId/*',  // Chapter sub-routes (edit mode)

  // --- Profile management (require auth) ---
  '/profile',             // Current user's profile settings
  '/profile/*',           // Profile sub-pages (editor, etc.)
  '/studio/*',            // Studio editing tools

  // --- User features (require auth) ---
  '/messages',            // Direct messages
  '/inbox',               // Notification inbox
  '/rewards',             // User rewards/earnings
  '/feed',                // Personalized feed
  '/tip-success',         // Tip payment confirmation
  '/reader-library',      // User's reading library
  '/saved',               // User's saved/bookmarked items

  // --- Redirects (legacy URL patterns → canonical) ---
  '/creator/:handle',     // Redirects to /u/:handle
  '/reader/:handle',      // Redirects to /u/:handle
  '/user/:handle',        // Redirects to /u/:handle
  '/@:handle',            // Redirects to /u/:handle

  // --- Misc authenticated features ---
  '/collab',              // Collaboration hub
  '/collab/*',            // Collaboration sub-routes
  '/analytics',           // Creator analytics
  '/verification',        // Identity verification submission
  '/email-testing',       // Dev/admin email testing tool
];

/**
 * STATIC ROUTES — Legal pages, public utilities.
 * Always accessible, no auth, no registry needed.
 */
const STATIC_ROUTES = [
  '/terms',               // Terms of Service
  '/privacy',             // Privacy Policy
  '/dmca',                // DMCA Policy
  '/content-guidelines',  // Content Guidelines
  '/search',              // Search results page
  '/art/:postId',         // Individual artwork view (public)
  '/community',           // Community page
  '/live',                // Live events page
  '/mature',              // Mature content gate
  '/human-verified',      // Human verification landing
  '/u/*',                 // Creator profiles (legacy renderer active)
];

/**
 * Combined exempt patterns — union of SYSTEM_ACTION_ROUTES + STATIC_ROUTES.
 * Any route matching these bypasses Page Registry enforcement.
 * Everything else MUST be canonical (registered + has definition).
 */
const EXEMPT_ROUTE_PATTERNS = [...SYSTEM_ACTION_ROUTES, ...STATIC_ROUTES];

// =============================================================================
// HOOKS
// =============================================================================

// Admin-only debug flag to use legacy renderer (default OFF)
const DEBUG_LEGACY_KEY = 'tc_admin_legacy_renderer_enabled';

/**
 * useLegacyRendererDebug - Admin-gated legacy renderer toggle
 * 
 * - Only admins can enable/read the flag
 * - Non-admins: flag is ignored and localStorage key is deleted if present
 * - Default is always OFF (canonical)
 * - Persists to localStorage for admin convenience
 */
export const useLegacyRendererDebug = () => {
  const [enabled, setEnabled] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  useEffect(() => {
    let admin = false;
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        admin = !!(user?.role === 'admin' || user?.is_admin);
      } catch (e) { /* ignore */ }
    }
    setIsAdmin(admin);
    
    if (admin) {
      // Admin: read stored preference
      const stored = localStorage.getItem(DEBUG_LEGACY_KEY);
      if (stored === 'true') {
        setEnabled(true);
      }
    } else {
      // Non-admin: force OFF and clean up any leftover key
      setEnabled(false);
      localStorage.removeItem(DEBUG_LEGACY_KEY);
    }
  }, []);
  
  const setLegacyEnabled = (value) => {
    if (!isAdmin) return; // Guard: non-admin cannot set
    const boolVal = !!value;
    localStorage.setItem(DEBUG_LEGACY_KEY, String(boolVal));
    setEnabled(boolVal);
  };
  
  const toggle = () => {
    if (!isAdmin) return; // Guard
    setLegacyEnabled(!enabled);
  };
  
  return { 
    legacyEnabled: isAdmin && enabled,
    isAdmin,
    setLegacyEnabled,
    toggle,
  };
};

const useIsAdmin = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        setIsAdmin(user?.role === 'admin' || user?.is_admin);
      } catch (e) {
        setIsAdmin(false);
      }
    }
  }, []);
  
  return isAdmin;
};

// =============================================================================
// HELPERS
// =============================================================================

const matchesAnyPattern = (pathname, patterns) => {
  for (const pattern of patterns) {
    if (pattern.endsWith('/*')) {
      const basePattern = pattern.slice(0, -2);
      if (pathname === basePattern || pathname.startsWith(basePattern + '/')) {
        return true;
      }
    }
    const match = matchPath({ path: pattern, end: true }, pathname);
    if (match) {
      return true;
    }
  }
  return false;
};

/**
 * Classify a route into its category for debugging/logging
 */
export const classifyRoute = (pathname) => {
  if (matchesAnyPattern(pathname, SYSTEM_ACTION_ROUTES)) return 'SYSTEM_ACTION';
  if (matchesAnyPattern(pathname, STATIC_ROUTES)) return 'STATIC';
  return 'CANONICAL';
};

// =============================================================================
// UI STATES
// =============================================================================

const NotFoundPage = ({ message = "Page not found" }) => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
    <div className="text-center max-w-md">
      <div className="w-16 h-16 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-6">
        <FileX className="w-8 h-8 text-zinc-500" />
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">404</h1>
      <p className="text-zinc-400 mb-6">{message}</p>
      <div className="flex gap-3 justify-center">
        <Button
          variant="outline"
          onClick={() => window.history.back()}
          className="border-zinc-700 text-zinc-300"
          data-testid="not-found-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Go Back
        </Button>
        <Button
          onClick={() => window.location.href = '/'}
          className="bg-orange-500 hover:bg-orange-600"
          data-testid="not-found-home-btn"
        >
          <Home className="w-4 h-4 mr-2" />
          Home
        </Button>
      </div>
    </div>
  </div>
);

const NoDefinitionAdminState = ({ page }) => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
    <div className="text-center max-w-lg">
      <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <AlertTriangle className="w-8 h-8 text-yellow-500" />
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">No Page Definition</h1>
      <p className="text-zinc-400 mb-2">
        This page is registered but has no Page Builder definition.
      </p>
      <p className="text-zinc-500 text-sm mb-6">
        Page: <span className="text-zinc-300 font-mono">{page?.pageId}</span> | 
        Type: <span className="text-zinc-300">{page?.pageType}</span>
      </p>
      <div className="flex gap-3 justify-center">
        <Button
          variant="outline"
          onClick={() => window.location.href = '/admin/page-builder'}
          className="border-zinc-700 text-zinc-300"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Page Builder
        </Button>
        <Button
          onClick={() => window.location.href = `/admin/page-builder/${page?.pageId}`}
          className="bg-orange-500 hover:bg-orange-600"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Definition
        </Button>
      </div>
    </div>
  </div>
);

const SafeDefaultTemplate = ({ page }) => (
  <div className="min-h-screen bg-zinc-950">
    <div className="border-b border-zinc-800">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <h1 className="text-3xl font-bold text-white">{page?.title || 'Page'}</h1>
        {page?.description && (
          <p className="text-zinc-400 mt-2">{page.description}</p>
        )}
      </div>
    </div>
    <div className="max-w-4xl mx-auto px-4 py-16 text-center">
      <div className="w-20 h-20 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
        <Construction className="w-10 h-10 text-orange-500" />
      </div>
      <h2 className="text-xl font-semibold text-white mb-3">Coming Soon</h2>
      <p className="text-zinc-400 max-w-md mx-auto">
        This page is being set up. Check back soon for updates.
      </p>
    </div>
  </div>
);

const LoadingState = () => (
  <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
  </div>
);

// =============================================================================
// MAIN PROVIDER
// =============================================================================

const CanonicalRouteProvider = ({ children }) => {
  const location = useLocation();
  const isAdmin = useIsAdmin();
  
  const [loading, setLoading] = useState(false);
  const [registryResult, setRegistryResult] = useState(null);
  const [checkComplete, setCheckComplete] = useState(false);
  
  const isExempt = useMemo(() => {
    return matchesAnyPattern(location.pathname, EXEMPT_ROUTE_PATTERNS);
  }, [location.pathname]);
  
  useEffect(() => {
    const checkRegistry = async () => {
      if (isExempt) {
        setCheckComplete(true);
        setRegistryResult(null);
        return;
      }
      
      setLoading(true);
      setCheckComplete(false);
      
      try {
        const slug = location.pathname;
        const apiSlug = slug === '/' ? '' : slug.replace(/^\//, '');
        const response = await axios.get(
          `/api/page-registry/check/${encodeURIComponent(apiSlug)}`
        );
        setRegistryResult(response.data);
      } catch (err) {
        console.error('Registry check error:', err);
        setRegistryResult({ registered: false, error: err.message });
      } finally {
        setLoading(false);
        setCheckComplete(true);
      }
    };
    
    checkRegistry();
  }, [location.pathname, isExempt]);
  
  // Exempt → render children directly
  if (isExempt) {
    return children;
  }
  
  if (loading || !checkComplete) {
    return <LoadingState />;
  }
  
  if (registryResult) {
    if (!registryResult.registered) {
      return <NotFoundPage message="This page is not registered in the Page Builder system." />;
    }
    
    if (registryResult.visibility === 'admin-only' && !isAdmin) {
      return <NotFoundPage message="Page not found" />;
    }
    
    if (registryResult.status !== 'published' && !isAdmin) {
      return <NotFoundPage message="This page is not yet available." />;
    }
    
    const hasDefinition = registryResult.has_definition || registryResult.has_draft || registryResult.has_published;
    
    if (!hasDefinition) {
      if (isAdmin) {
        return <NoDefinitionAdminState page={registryResult} />;
      }
      return <SafeDefaultTemplate page={registryResult} />;
    }
  }
  
  const extractedParams = registryResult?.extractedParams || {};
  
  return (
    <RouteParamsContext.Provider value={{ 
      ...extractedParams, 
      pageId: registryResult?.pageId,
      isDynamic: registryResult?.is_dynamic,
      pageType: registryResult?.pageType,
    }}>
      {children}
    </RouteParamsContext.Provider>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export const ROUTE_CONFIG = {
  systemAction: SYSTEM_ACTION_ROUTES,
  static: STATIC_ROUTES,
  exempt: EXEMPT_ROUTE_PATTERNS,
};

export default CanonicalRouteProvider;
export { 
  NotFoundPage, 
  SafeDefaultTemplate, 
  NoDefinitionAdminState, 
  LoadingState,
  EXEMPT_ROUTE_PATTERNS,
  SYSTEM_ACTION_ROUTES,
  STATIC_ROUTES,
  matchesAnyPattern,
};
